import { createBreakpoints } from "@chakra-ui/theme-tools"

const bp = [0, 30, 48, 62, 80, 96].map(em => em * 16);

const chakraBpNames = ['sm', 'md', 'lg', 'xl', '2xl']
export const breakpoints = chakraBpNames.reduce((bps, name, i) => {
  bps[name] = `${bp[i + 1]}px`
  return bps
}, {})

const sans = '"Noto Sans TC", sans-serif';
const serif = '"Noto Serif TC", serif';
const fonts = {
  heading: sans,
  body: sans,
  mono: "Menlo, monospace",
  sans,
  serif,
}

const colors = {
  gold:
  {
    50: '#fcf4e5',
    100: '#eadec6',
    200: '#dac8a5',
    300: '#cbb382',
    400: '#bb9d60',
    500: '#a18346',
    600: '#7e6635',
    700: '#5a4925',
    800: '#372c14',
    900: '#150f00',
  },
  brown:
  {
    50: '#ffefe5',
    100: '#ecd3c7',
    200: '#d8b6a6',
    300: '#c79b84',
    400: '#b67e63',
    500: '#9c6549',
    600: '#7b4e38',
    700: '#583727',
    800: '#372115',
    900: '#1a0800',
  },
  "purple":
  {
    50: '#f3ebff',
    100: '#d6c9ec',
    200: '#b9a6da',
    300: '#9d83ca',
    400: '#8160b9',
    500: '#67469f',
    600: '#50367d',
    700: '#39275b',
    800: '#221638',
    900: '#0d0619',
  },
  "yellow":
  {
    50: '#fff4dc',
    100: '#fee0b1',
    200: '#facc83',
    300: '#f8b753',
    400: '#f6a324',
    500: '#dd890d',
    600: '#ac6b07',
    700: '#7b4c03',
    800: '#4b2e00',
    900: '#1d0e00',
  },
  "red": {
    50: '#ffe7e7',
    100: '#f4c1c2',
    200: '#e69a9b',
    300: '#da7274',
    400: '#ce4b4d',
    500: '#b43133',
    600: '#8d2527',
    700: '#65191b',
    800: '#3f0d0f',
    900: '#1c0103',
  },
  "orange":
  {
    50: '#ffe8e3',
    100: '#fdc2b7',
    200: '#f59989',
    300: '#f0725c',
    400: '#eb4b2e',
    500: '#d13114',
    600: '#a3260f',
    700: '#761a0a',
    800: '#480e03',
    900: '#1e0100',
  },
  "green":
  {
    50: '#ecfae5',
    100: '#d2eac5',
    200: '#b6daa2',
    300: '#9acc7f',
    400: '#7ebd5c',
    500: '#64a342',
    600: '#4d7f32',
    700: '#375b23',
    800: '#1e3712',
    900: '#051400',
  },
  "teal": {
    "50": "#EAF8FA",
    "100": "#C4EBF2",
    "200": "#9FDFEA",
    "300": "#79D2E2",
    "400": "#53C6DA",
    "500": "#2DB9D2",
    "600": "#2494A8",
    "700": "#1B6F7E",
    "800": "#124A54",
    "900": "#09252A"
  },
  "cyan": {
    "50": "#E5FBFF",
    "100": "#B8F5FF",
    "200": "#8AEEFF",
    "300": "#5CE7FF",
    "400": "#2EE0FF",
    "500": "#00DAFF",
    "600": "#00AECC",
    "700": "#008399",
    "800": "#005766",
    "900": "#002C33"
  },
  "blue":
  {
    50: '#e0f7ff',
    100: '#bbe3f5',
    200: '#94ceea',
    300: '#6bbbe0',
    400: '#45a8d6',
    500: '#2d8ebd',
    600: '#1f6e94',
    700: '#124f6a',
    800: '#022f42',
    900: '#00121b',
  },
  "pink":
  {
    50: '#ffe6ed',
    100: '#f7bbc9',
    200: '#ef8fa5',
    300: '#e86382',
    400: '#e2395e',
    500: '#c92244',
    600: '#9d1a35',
    700: '#6f1126',
    800: '#440816',
    900: '#1b0106',
  },
  "gray": {
    "50": "#F2F2F2",
    "100": "#DBDBDB",
    "200": "#C4C4C4",
    "300": "#ADADAD",
    "400": "#969696",
    "500": "#808080",
    "600": "#666666",
    "700": "#4D4D4D",
    "800": "#333333",
    "900": "#1A1A1A"
  },
  dark:
  {
    50: '#f8f1f0',
    100: '#d9d9d9',
    200: '#bfbfbf',
    300: '#a6a6a6',
    400: '#8c8c8c',
    500: '#737373',
    600: '#595959',
    700: '#404040',
    800: '#262626',
    900: '#120b0c',
  },
  ci: {
    purple: {
      50: '#ffe9ff',
      100: '#efc3ee',
      200: '#e29de0',
      300: '#d576d2',
      400: '#c850c4',
      500: '#af37ab',
      600: '#892a86',
      700: '#631d60',
      800: '#3d103b',
      900: '#180317',
    },
    teal: {
      50: '#d8f7ff',
      100: '#acf1ff',
      200: '#7defff',
      300: '#4df0ff',
      400: '#27f4ff',
      500: '#17e4e6',
      600: '#00b3af',
      700: '#007b80',
      800: '#00454e',
      900: '#00181c',
    }
  },
}

const customTheme = {
  fonts,
  breakpoints: createBreakpoints(breakpoints),
  colors: {
    ...colors,
    custom: {
      primary: colors.teal[500],
    }
  },
  sizes: {
    container: {
      xs: '480px',
    },
  },
  components: {
    Button: {
      defaultProps: {
        colorScheme: 'teal',
      },
      variants: {
        solid: (props) => {
          const mainColor = props.colorScheme === 'gray' ? `${props.colorScheme}.100` : `${props.colorScheme}.500`
          const hoverColor = `${props.colorScheme}.500`
          return {
            border: "2px solid",
            borderColor: mainColor,
            bg: mainColor,
            _hover: {
              bg: 'transparent',
              borderColor: hoverColor,
              color: hoverColor,
              _disabled: {
                bg: 'transparent',
              },
            },
            _active: {
              bg: 'transparent',
              borderColor: hoverColor,
              color: hoverColor,
            },
            _focus: {
              bg: 'transparent',
              borderColor: hoverColor,
              color: hoverColor,
            },
          }
        },
        outline: (props) => ({
          border: "2px solid",
          borderColor: `${props.colorScheme}.500`,
          _hover: {
            bg: `${props.colorScheme}.500`,
            color: 'white',
            border: "2px solid",
            borderColor: `${props.colorScheme}.500`,
          },
          _active: {
            bg: `${props.colorScheme}.500`,
            color: 'white',
            border: "2px solid",
            borderColor: `${props.colorScheme}.500`,
          },
          _focus: {
            bg: `${props.colorScheme}.500`,
            color: 'white',
            border: "2px solid",
            borderColor: `${props.colorScheme}.500`,
          },
        }),
        ghost: (props) => ({
          border: "none",
          _hover: {
            color: `${props.colorScheme}.500`,
          },
          _focus: {
            color: `${props.colorScheme}.500`,
          },
          _active: {
            bg: `${props.colorScheme}.200`,
          },
        }),
        form: props => {
          if (!props.colorScheme || props.colorScheme === 'gray') {
            return {
              border: "none",
              color: 'gray.600',
              bg: 'gray.50',
              _hover: {
                bg: 'gray.100',
              },
            }
          }
          return {
            border: "none",
            color: `${props.colorScheme}.600`,
            bg: 'tranparent',
            _hover: {
              bg: `${props.colorScheme}.100`,
            },
          }
        },
        staticSolid: (props) => {
          const mainColor = props.colorScheme === 'gray' ? `${props.colorScheme}.100` : `${props.colorScheme}.500`
          const hoverColor = `${props.colorScheme}.600`
          return {
            border: "2px solid",
            borderColor: mainColor,
            bg: mainColor,
            color: 'white',
            _hover: {
              bg: hoverColor,
              borderColor: hoverColor,
              color: 'white',
            },
            _active: {
              bg: hoverColor,
              borderColor: hoverColor,
              color: 'white',
            },
            _active: {
              bg: hoverColor,
              borderColor: hoverColor,
              color: 'white',
            },
          }
        },
      },
    },
    Container: {
      baseStyle: {
        maxW: 'container.xs',
      },
    },
    Modal: {
      baseStyle: props => ({
        dialog: {
          maxH: props.scrollBehavior === "inside" ? "calc(100% - 4rem)" : undefined,
          my: '2rem',
        }
      })
    },
  },
  styles: {
    body: {
      bg: '#f8f8f8',
    },
    global: {
      "*, *::before, &::after": {
        borderColor: '#D9D9D9',
      }
    },
  },
}

export default customTheme
